import React, {useEffect, useState} from 'react';
import {Container, Col, Row, Button} from 'reactstrap';
import {subscribeToCampaign} from 'react-controlled-ab/datalayers/vwo';

import {usePhaseState} from 'funnel-schedule/phase-context';
import {useUserState} from 'contexts/user';
import useBalanceText from 'utils/useBalanceText';
import useBeacon from 'utils/useBeacon';
import track from 'utils/track';

import PageMeta from 'components/PageMeta';
import SectionCL from 'components/ComponentLibrary/SectionCL';
import Webinar from 'components/Webinar';
import Video from 'components/Video';
import SalesCopy from 'components/SalesCopy';
import RegistrationModal from 'components/RegistrationModal';
import Footer from 'components/Elements/Footer';
import ExitModal from 'components/ComponentLibrary/ExitModal';

const COMPLETED_MASTERCLASS_EVENT = 'Completed Watching TT2 Masterclass';

export default function ScreeningIndex({replayOverride = false, ...props}) {
  const {
    phases: {
      default: defaultSchedule = {
        phase: 'before'
      },
      webinar: webinarSchedule = {
        phase: 'before'
      }
    }
  } = usePhaseState();
  const {bucket, events = []} = useUserState();
  const [anchorScrolled, setAnchorScrolled] = useState(false);
  const hasCompletedMasterclass =
    events.includes(COMPLETED_MASTERCLASS_EVENT) ||
    webinarSchedule.phase === 'replay' ||
    webinarSchedule.phase === 'after';

  const salesSectionRevealed = hasCompletedMasterclass || replayOverride;

  const productID = 'C-TT2';
  const productSku = 'C-TT2';
  const productName = 'Tackling Type 2';
  const productSlug = 'tt2-masterclass';
  const productValue = 247;
  const paymentPlanPrice = 89;

  useEffect(() => {
    if (hasCompletedMasterclass)
      track('Product Viewed', {
        product_id: productID,
        sku: productSku,
        name: productName,
        variant: productSlug,
        value: productValue,
        currency: 'usd'
      });
  }, [hasCompletedMasterclass]);

  useEffect(() => {
    if (anchorScrolled) return;
    if (events.includes(COMPLETED_MASTERCLASS_EVENT)) {
      setAnchorScrolled(true);
      if (window !== undefined && window.location.hash === '#join') {
        document.getElementById('watch-cta-thankyou').scrollIntoView();
      }
    }
  }, [events, anchorScrolled]);

  const webinarHeading = (
    <Row className="text-center">
      <Col>
        <img
          className="mb-2"
          src="https://cdn.foodrevolution.org/tt2/tt2m-logo-2024.svg"
          alt="tackling type 2 masterclass text logo"
          width={300}
          height={66}
        />
        <h5 className="mt-0 text-400">with Brenda Davis, RD, & Ocean Robbins</h5>
        <h2 className="mt-0">4 Diabetes Myths & 10 Groundbreaking Truths</h2>
      </Col>
    </Row>
  );

  const webinarFooter = (
    <Row>
      <Col>
        <h4 className="text-center text-600 mb-0">
          <a
            href="https://cdn.foodrevolution.org/members/pdf/tt2/Tackling%20Type%202%20Masterclass%20Workbook.pdf"
            className="workbook-download text-underline"
            target="_blank"
          >
            Download your FREE workbook here.
          </a>
        </h4>
        <p className="text-center balance-text mt-4 m-0">
          IMPORTANT:{' '}
          {
            {
              prevent: (
                <>Taking the time to learn how to prevent diabetes is the most valuable thing you can do today.</>
              ),
              optimize: (
                <>Taking the time to learn how to end prediabetes is the most valuable thing you can do today.</>
              ),
              reverse: (
                <>
                  Taking the time to learn how to reverse type 2 diabetes is the most valuable thing you can do today.
                </>
              )
            }[bucket]
          }
        </p>
      </Col>
    </Row>
  );

  useBalanceText();
  useBeacon();
  return (
    <div className="page page-nosocial d-flex flex-column">
      <PageMeta title="Watch the Tackling Type 2 Masterclass with Brenda Davis, RD" />
      <div className="page-content">
        <SectionCL
          id="screening-header-cta"
          color="white"
          angle={salesSectionRevealed ? 'bottom' : 'none'}
          order="first"
        >
          <Container>
            {replayOverride ? (
              <>
                {webinarHeading}
                <Row className="d-flex justify-content-center">
                  <Col lg="10" className="video-col px-0 px-sm-3">
                    <Video
                      url="https://customer-ros8xhsmbkksflna.cloudflarestream.com/7bb0d442ebebb3f8e246326fa766a24c/watch"
                      label="TT2 - Masterclass"
                    />
                  </Col>
                </Row>
                {webinarFooter}
              </>
            ) : (
              <>
                <Webinar
                  noun="Masterclass"
                  headingSection={webinarHeading}
                  footerSection={webinarFooter}
                  label="TT2 - Masterclass"
                />
              </>
            )}
          </Container>
        </SectionCL>

        {salesSectionRevealed && (
          <>
            <SalesCopy
              version="screening"
              productId={productID}
              productSku={productSku}
              productSlug={productSlug}
              productPrice={productValue}
              paymentPlanPrice={paymentPlanPrice}
            />
          </>
        )}
      </div>

      {!replayOverride && (
        <>
          <RegistrationModal />
          <ExitModal
            exitDelay={30000}
            content={
              <>
                {hasCompletedMasterclass ? (
                  <>
                    <h4 className="section-heading mb-4">Before you go…</h4>
                    <p>
                      This is your one-time chance to <br className="d-none d-xl-block" />
                      get <i>Tackling Type 2</i> for 50% off.
                    </p>
                    <p className="mb-0">
                      Get all the diabetes-busting recipes, <br className="d-none d-xl-block" />
                      life-changing lessons, community, and <br className="d-none d-xl-block" />
                      ongoing support for the lowest price ever!
                    </p>
                  </>
                ) : (
                  <>
                    <p>
                      It’s possible to <b>prevent</b> and even <b>reverse</b> type 2 diabetes. Please don’t miss this
                      information.
                    </p>
                    <p>
                      <b>Check your email for the link to come back to this page at any time!</b>
                    </p>
                  </>
                )}
              </>
            }
            image={{
              url: hasCompletedMasterclass
                ? 'https://cdn.foodrevolution.org/tt2/tt2-product-image-v20240207.png'
                : require('static/backgrounds/1331365061-exit.jpg'),
              alt: '',
              width: hasCompletedMasterclass ? 1500 : 1200,
              height: hasCompletedMasterclass ? 711 : 960
            }}
            imageBackground={false}
            positiveButtonText={hasCompletedMasterclass ? 'Yes, I want to protect my health' : 'Keep Watching'}
            positiveButtonColor="exit-orange"
            negativeButtonText={hasCompletedMasterclass ? 'No thanks, I’ll take my chances' : 'No thanks'}
            scrollHref={hasCompletedMasterclass ? 'course-cta-screening' : 'screening-header-cta'}
            scrollOffset={0}
          />
        </>
      )}

      <div className="footer-content mt-auto">
        <Footer />
      </div>
    </div>
  );
}
